<!-- eslint-disable no-irregular-whitespace -->
<script setup>
import { ref, watch, computed } from 'vue';
import { ArrowUp } from 'lucide-vue-next';
import { i18n } from '@/i18n';

const props = defineProps({
  scene: Object,
});

const emit = defineEmits(['click']);

const promptExamples = ref([]);

const isChineseLocale = computed(() => {
  return i18n.isChinese;
});

const examplesByTagChinese = {
  poster: [
    {
      id: 3,
      label: '艺术展览',
      prompt: "制作一个典雅的艺术展览海报，展览名称'当代艺术画展'，展期一个月",
    },
    {
      id: 2,
      label: '教育培训机构',
      prompt: `设计一张专业的教育培训机构品牌海报，分为两个视觉层次：「背景图」和「前景文字排版」。
文字排版风格：现代主义风格，使用清晰、无衬线字体，网格布局，具有层次感。
保持良好留白，文字不要遮挡关键图像区域。
内容如下（如果空白就你来补充，改成中文）：
机构名称：智慧教育培训中心
教育理念：激发潜能，成就未来 - 专业师资，个性化教学
课程时间：周一至周日 9:00-21:00 / 寒暑假集训营
特色课程：少儿编程、创意美术、英语口语、数学思维、科学实验
联系方式：咨询热线 400-888-9999 / 地址：市中心教育大厦8楼
配色方案：清新的蓝色调配合白色，营造专业学术氛围`,
    },
    {
      id: 1,
      label: '时尚展示',
      prompt: `设计一张活动海报，分为两个视觉层次：「背景图」和「前景文字排版」。
文字排版风格：现代主义风格，使用清晰、无衬线字体，网格布局，具有层次感。
保持良好留白，文字不要遮挡关键图像区域。
内容如下（如果空白就你来补充，改成中文）：活动主题：活动时间：活动地点：邀请嘉宾（用三个照片）：`,
    },
  ],
  'social-media': [
    {
      id: 1,
      label: '教育课程',
      prompt: `设计一个教育课程社交媒体图片，分为两个视觉层次：「背景图」和「前景文字排版」。

一、设计风格与布局
• 风格定位：简洁明快的现代教育风格
• 配色方案：温暖的橙色调配合白色，营造活力学习氛围
• 版面布局：网格化设计，保持良好留白，文字不遮挡关键图像区域

二、视觉元素要求
• 创意绘画元素：彩色画笔、调色板、儿童画作展示
• 学习进度标记：进度条、徽章、星级评价系统
• 装饰图案：几何形状点缀，增强视觉层次

三、文字内容结构
课程名称：艺术启蒙课堂
课程特色：激发创意思维，培养艺术素养
适合年龄：3-8岁儿童
课程亮点：专业美术老师指导、小班教学、作品展示
报名信息：每周六日开课 / 免费试听课程

四、技术要求
• 尺寸适配：社交媒体标准尺寸
• 字体规范：清晰易读的无衬线字体，层次分明
• 品质要求：高清输出，色彩饱和度适中`,
    },
    {
      id: 2,
      label: '健身打卡',
      prompt: `设计一个健身打卡社交媒体分享图，分为两个视觉层次：「背景图」和「前景文字排版」。

一、设计风格与布局
• 风格定位：活力运动风格，现代简约设计
• 配色方案：活力橙色渐变配合深蓝色，营造动感氛围
• 版面布局：网格化设计，保持良好留白

二、视觉元素要求
• 背景：健身房环境
• 进度展示：30天打卡进度条或日历标记

三、文字内容结构
挑战主题：30天健身挑战
激励文案：每一次挥汗都是对自己的超越
打卡天数：第15天 / 共30天
运动成果：燃烧卡路里500+ / 运动时长45分钟
鼓励标语：坚持就是胜利，明天继续加油！`,
    },
    {
      id: 3,
      label: '读书笔记',
      prompt:
        '参考版式，制作一个文艺清新的读书笔记分享图。需要有配图和文字，内容是不同的读书笔记。[SeedeImage##https://static.seedeai.com/asset/d2c7717b-51ac-437f-a3cb-05177a7ba8d8]',
    },
  ],
  infographic: [
    {
      id: 1,
      label: '市场趋势',
      prompt: "制作一个数据驱动的市场趋势信息图，主题为'2025年电商增长报告'",
    },
    {
      id: 2,
      label: '知识科普',
      prompt: "设计一个教育主题的知识科普图表，内容为'垃圾分类指南'",
    },
    {
      id: 3,
      label: '环保统计',
      prompt: "创建一个环保主题的统计信息图，标题'全球碳排放现状'",
    },
  ],
  scrollytelling: [
    {
      id: 1,
      label: '品牌故事长图',
      prompt: `设计一个品牌故事的滚动讲述长图，分为多个章节，每章配有插画和简洁文字，整体风格温暖治愈

一、整体风格（Overall Style）
属性	内容
风格定位	山野美学 × 小众杂志感 × 清新自然人文风
关键词	green lifestyle, summer escape, wild elegance, slow travel, photography zine, rural poetry
设计气质	轻盈、留白、疏密有致、极简中带有手感与质地感
适用场景	节气宣传、乡野旅拍、品牌摄影展览页、文艺风PPT、自然治愈课程页`,
    },
    {
      id: 2,
      label: '狗粮的商品详情页',
      prompt: `模仿做一个【狗粮的商品详情页】要求: 
1. 请运用圆角、多形状、渐变、模糊等效果，丰富页面的视觉效果和信息层级，采用更高级现代的设计风格 
2. 手机端适配，字体要大一些，所有文字至少大于 32 px
3. 优化布局结构，生成内容要撑满海报页面尺寸，注意文字的行距、段落间距、页边距，整体美观，可以采用网格系统，避免出现大面积空白区域的情况，禁止出现文字重叠的情况。【提示，可以通过更改文字大小，行距，来实现。铺满尺寸是最高优先级】
4. 内容主题修改成【狗粮】，其他内容自己发挥

价格或促销信息（如有）：618 大促限时 5 折优惠

@SeedeMaterial({"url":"https://static.seedeai.com/asset/692a77e6-7a42-4997-9e6b-eb58a09c0fb3","width":1024,"height":1024,"aspectRatio":1,"tag":"Logo"})
  `,
    },
    {
      id: 3,
      label: '公益科普长图',
      prompt:
        '制作一个环保公益主题的长图，内容包括数据可视化、故事插画和行动号召，风格清新易懂，适合社交媒体传播',
    },
  ],
  presentation: [
    {
      id: 1,
      label: '项目汇报',
      prompt: `生成一份现代、时尚、大气且高级的品牌手册（PPT格式），包含以下5个核心模块：
1. 品牌故事（Brand Story）品牌起源、愿景、使命、价值观简洁有力的文案 + 高质量视觉（如创始人故事、品牌历程时间轴）
2.品牌视觉识别（Visual Identity）主/辅色彩系统（Pantone色号+应用场景）品牌LOGO（标准制图+使用规范）字体系统（中英文标准字体+组合示例）图形/图案延展（如纹理、辅助图形）
3.产品与服务（Products & Services）核心产品线展示（高精度白底图+场景化应用图）服务流程/技术优势（信息图表呈现）
4.品牌调性（Brand Tone）关键词提炼（如‘极简’‘未来感’‘可持续’）视觉风格参考（情绪板：材质/光影/场景灵感图）品牌语言规范（文案风格示例：专业/亲和/先锋等）
5.应用场景（Applications）合作案例展示（联名/活动海报等）
设计要求：排版：留白充足，采用非对称布局+网格系统配色：低饱和度主色（如高级灰、莫兰迪色）+ 金属质感点缀 + 细节纹理字体：无衬线字体（如Helvetica Neue、中文用思源黑体）`,
    },
    {
      id: 2,
      label: '产品发布',
      prompt: "创建一个创意十足的产品发布演示，产品为'智能家居系统'",
    },
    {
      id: 3,
      label: '财务报告',
      prompt: "制作一个专业的财务报告演示文稿，标题'2025年度财务分析'",
    },
  ],
  'pull-up-banner': [
    {
      id: 1,
      label: '环保倡议易拉宝',
      prompt: `设计一个环境保护主题的易拉宝，需包含以下核心内容：

主视觉区：
展现人与自然和谐共生的场景（如森林/海洋/绿地与人类的互动）
使用绿色系为主色调，搭配自然元素图标

核心信息层：
▫️顶部标语：突出“守护地球”“绿色未来”等关键词
▫️中部主体：

保护环境的3个具体行动方案（如垃圾分类/节能减排/植树造林）

每个方案配1个简约图标
▫️底部号召：
强调个人行动的重要性
加入可扫描的环保承诺二维码

设计规范：
使用环保再生材料质感背景
文字与图片占比保持6:4
重要数据用绿色加粗突出

视觉要求：
避免使用塑料制品图像
采用扁平化设计风格
确保3米外能清晰识别主标语
请体现“每个人都是环保守护者”的理念，通过视觉设计引发观众的情感共鸣和行为改变。
`,
    },
    {
      id: 2,
      label: '产品发布',
      prompt: `为 Seede App 设计一个易拉宝：
主视觉区：
场景描绘：展现一群多元化、充满活力的年轻人在都市、自然或休闲场景中通过手机互联的瞬间，体现“连接真实兴趣”的理念
主色调：使用活力蓝或渐变霓虹色为主色调，搭配抽象社交互动元素（如对话气泡、心跳波线、连接节点等图标）
核心信息层：

▫️顶部标语：突出“找到同频的人”“开启真实社交”等关键词，例如：
“在这里，遇见和你一样的人”

▫️中部主体：
推荐理由1：精准兴趣匹配（配简约爱好图标，如音乐、运动、旅行等）
推荐理由2：无压力破冰聊天（配对话气泡+表情图标）
推荐理由3：线下同城活动邀约（配地图定位+人群图标）

▫️底部号召：
强调“不再孤独，从一次相遇开始”
加入App下载二维码，标注“扫码开启你的社交新圈”`,
    },
    {
      id: 3,
      label: '财务报告',
      prompt: "制作一简约的易拉宝设计，标题'2025年上海车展招商'",
    },
  ],
  brochure: [
    {
      id: 1,
      label: '企业宣传册',
      prompt: `设计一套企业宣传册，共8页，包含封面、公司简介、核心业务、团队介绍、成功案例、企业文化、联系方式和封底。

设计风格：现代商务风格，专业大气
配色方案：企业蓝色调配合白色和灰色，营造专业可信赖的氛围
版面布局：网格化设计，保持良好留白，图文比例4:6

模拟内容：
公司名称：创新科技有限公司
公司简介：成立于2015年，专注于企业数字化转型解决方案
核心业务：云计算服务、AI智能应用、企业管理系统
团队规模：200+专业技术人员
成功案例：服务500+企业客户，覆盖金融、教育、零售等行业
企业文化：创新、专业、共赢
联系方式：电话 400-123-4567 / 邮箱 info@company.com / 地址：北京市朝阳区科技园`,
    },
    {
      id: 2,
      label: '旅行行程单',
      prompt: `制作一份云南7日游行程宣传册，包含封面、行程概览、每日详细安排（景点、住宿、餐饮）、旅行贴士和预订信息。

设计风格：清新自然风格，充满旅行氛围
配色方案：天空蓝、自然绿配合暖色调，营造轻松愉悦的度假感
版面布局：图片为主，文字为辅，图文比例6:4

模拟内容：
行程主题：云南风情7日深度游
出发日期：2025年4月1日
行程亮点：大理古城、丽江古镇、玉龙雪山、泸沽湖、香格里拉
住宿标准：四星级酒店+特色客栈
餐饮安排：含早餐，特色餐饮（过桥米线、烤乳扇、酥油茶）
交通方式：旅游大巴+部分景区观光车
价格：3999元/人起
预订热线：400-888-6666`,
    },
    {
      id: 3,
      label: '产品手册',
      prompt: `设计一本智能手表产品手册，包含封面、产品特性、技术参数、使用场景、配件介绍和售后服务。

设计风格：科技感现代风格，简约时尚
配色方案：黑色、银色、科技蓝，营造高端科技产品质感
版面布局：产品图片突出展示，技术参数清晰排版，图文比例5:5

模拟内容：
产品名称：SmartWatch Pro X1
核心特性：健康监测、运动追踪、智能通知、长续航
技术参数：
- 屏幕：1.4英寸AMOLED高清显示屏
- 续航：7天超长续航
- 防水：5ATM防水等级
- 传感器：心率、血氧、加速度、陀螺仪
- 连接：蓝牙5.0、GPS定位
使用场景：运动健身、日常通勤、商务会议、户外探险
配件清单：充电底座、说明书、保修卡
售后服务：1年质保、30天无理由退换、全国联保
价格：1299元`,
    },
  ],
  flyer: [
    {
      id: 1,
      label: '促销活动传单',
      prompt: `设计一张超市促销活动传单，宣传周年庆大促销活动。

设计风格：活力促销风格，吸引眼球
配色方案：红色、黄色为主色调，营造热烈促销氛围
版面布局：主视觉突出，促销信息清晰，图文比例5:5

模拟内容：
活动主题：周年庆感恩回馈
活动时间：2025年4月1日-4月7日
促销内容：
- 全场商品满200减50
- 生鲜水果5折起
- 日用百货买二送一
- 会员专享额外9折
特别优惠：前100名顾客送精美礼品
门店地址：北京市朝阳区购物中心1层
咨询电话：400-123-4567`,
    },
    {
      id: 2,
      label: '餐厅开业传单',
      prompt: `制作一张新餐厅开业宣传传单，介绍餐厅特色和开业优惠。

设计风格：美食诱人风格，温馨舒适
配色方案：暖色调为主，橙色、棕色搭配，营造食欲感
版面布局：美食图片为主，文字信息简洁，图文比例6:4

模拟内容：
餐厅名称：味蕾时光餐厅
开业时间：2025年4月15日盛大开业
餐厅特色：融合中西美食，精选优质食材，大厨匠心烹制
招牌菜品：秘制烤鱼、手工披萨、特色火锅、创意甜品
开业优惠：
- 开业当天全场8折
- 前50名顾客赠送精美甜品
- 办理会员卡充500送100
营业时间：10:00-22:00
地址：北京市海淀区美食街88号
订餐电话：010-8888-6666`,
    },
    {
      id: 3,
      label: '教育培训传单',
      prompt: `设计一张少儿英语培训机构招生传单，突出课程特色和优惠政策。

设计风格：活泼可爱风格，充满童趣
配色方案：蓝色、绿色、黄色搭配，营造轻松学习氛围
版面布局：卡通元素点缀，信息层次分明，图文比例4:6

模拟内容：
机构名称：快乐英语学堂
招生对象：3-12岁儿童
课程特色：
- 外教小班授课（8人精品班）
- 沉浸式英语环境
- 趣味互动教学法
- 定期学习成果展示
课程体系：启蒙班、基础班、进阶班、强化班
师资力量：持证外教+中教辅导，平均教龄5年以上
优惠活动：
- 现在报名享8折优惠
- 推荐好友双方各减200元
- 免费试听课程2节
校区地址：北京市朝阳区教育园区3号楼
咨询热线：400-999-8888`,
    },
  ],
};

const examplesByTagEnglish = {
  poster: [
    {
      id: 3,
      label: 'Art Exhibition',
      prompt:
        "Create an elegant art exhibition poster, exhibition name 'Contemporary Art Gallery', duration one month",
    },
    {
      id: 1,
      label: 'Tech Conference',
      prompt:
        "Create a modern minimalist tech conference poster, title '2025 AI Design Summit', location Beijing",
    },
    {
      id: 2,
      label: 'Music Festival',
      prompt:
        "Design a vibrant music festival promotional poster, theme 'Spring Music Festival', date March 15th",
    },
  ],
  'social-media': [
    {
      id: 1,
      label: 'Food Sharing',
      prompt: "Design a warm food sharing social media image, theme 'Home Recipe Sharing'",
    },
    {
      id: 2,
      label: 'Business Event',
      prompt:
        "Create a professional business event promotional image, event name 'Entrepreneurs Summit 2025'",
    },
    {
      id: 3,
      label: 'Product Launch',
      prompt:
        "Create a stylish product launch social media image, new product 'Spring Limited Edition Perfume'",
    },
  ],
  scrollytelling: [
    {
      id: 1,
      label: 'Brand Story Scrollytelling',
      prompt: `Design a scrollytelling long image for a brand story, divided into multiple chapters, each with illustrations and concise text. The overall style is warm and healing.

1. Overall Style
Attribute    Content
Style Positioning    Mountain Aesthetics × Indie Magazine Vibe × Fresh Natural Humanistic Style
Keywords    green lifestyle, summer escape, wild elegance, slow travel, photography zine, rural poetry
Design Character    Light, whitespace, balanced density, minimalism with tactile and textured feel
Applicable Scenarios    Seasonal promotions, countryside travel photography, brand photo exhibition pages, artistic PPTs, nature healing course pages`,
    },
    {
      id: 2,
      label: 'Product Demo Interactive Page',
      prompt:
        'Create a long image for product feature demonstration, using step-by-step animation and visual guidance to highlight product features. Bright colors, suitable for online promotion.',
    },
    {
      id: 3,
      label: 'Public Welfare Science Long Image',
      prompt:
        'Produce a long image with an environmental public welfare theme, including data visualization, story illustrations, and calls to action. The style is fresh and easy to understand, suitable for social media sharing.',
    },
  ],
  infographic: [
    {
      id: 1,
      label: 'Market Trends',
      prompt:
        "Create a data-driven market trend infographic, theme '2025 E-commerce Growth Report'",
    },
    {
      id: 2,
      label: 'Educational Content',
      prompt: "Design an educational knowledge infographic, content 'Waste Sorting Guide'",
    },
    {
      id: 3,
      label: 'Environmental Stats',
      prompt:
        "Create an environmental statistics infographic, title 'Global Carbon Emissions Status'",
    },
  ],
  presentation: [
    {
      id: 1,
      label: 'Project Report',
      prompt: "Design a business-style project report PPT cover, project name 'Q4 Sales Summary'",
    },
    {
      id: 2,
      label: 'Product Launch',
      prompt: "Create a creative product launch presentation, product 'Smart Home System'",
    },
    {
      id: 3,
      label: 'Financial Report',
      prompt:
        "Create a professional financial report presentation, title '2025 Annual Financial Analysis'",
    },
  ],
  brochure: [
    {
      id: 1,
      label: 'Corporate Brochure',
      prompt: `Design a corporate brochure with 8 pages, including cover, company introduction, core business, team introduction, success cases, corporate culture, contact information, and back cover.

Design Style: Modern business style, professional and elegant
Color Scheme: Corporate blue with white and gray, creating a professional and trustworthy atmosphere
Layout: Grid-based design, maintaining good white space, image-to-text ratio 4:6

Sample Content:
Company Name: Innovation Technology Co., Ltd.
Company Introduction: Founded in 2015, focusing on enterprise digital transformation solutions
Core Business: Cloud computing services, AI intelligent applications, enterprise management systems
Team Size: 200+ professional technical personnel
Success Cases: Serving 500+ enterprise clients, covering finance, education, retail and other industries
Corporate Culture: Innovation, Professionalism, Win-Win
Contact Information: Phone 400-123-4567 / Email info@company.com / Address: Science and Technology Park, Chaoyang District, Beijing`,
    },
    {
      id: 2,
      label: 'Travel Itinerary',
      prompt: `Create a 7-day Yunnan tour itinerary brochure, including cover, itinerary overview, daily detailed arrangements (attractions, accommodation, dining), travel tips, and booking information.

Design Style: Fresh and natural style, full of travel atmosphere
Color Scheme: Sky blue, natural green with warm tones, creating a relaxed and pleasant vacation feel
Layout: Image-focused with supporting text, image-to-text ratio 6:4

Sample Content:
Itinerary Theme: 7-Day In-Depth Yunnan Cultural Tour
Departure Date: April 1, 2025
Highlights: Dali Ancient City, Lijiang Old Town, Jade Dragon Snow Mountain, Lugu Lake, Shangri-La
Accommodation Standard: Four-star hotels + characteristic inns
Dining Arrangements: Breakfast included, specialty dining (Crossing Bridge Noodles, Grilled Rushan, Butter Tea)
Transportation: Tourist bus + scenic area shuttle
Price: Starting from 3999 RMB/person
Booking Hotline: 400-888-6666`,
    },
    {
      id: 3,
      label: 'Product Manual',
      prompt: `Design a smartwatch product manual, including cover, product features, technical specifications, usage scenarios, accessories introduction, and after-sales service.

Design Style: Tech-modern style, minimalist and fashionable
Color Scheme: Black, silver, tech blue, creating a premium tech product feel
Layout: Product images prominently displayed, technical specifications clearly arranged, image-to-text ratio 5:5

Sample Content:
Product Name: SmartWatch Pro X1
Core Features: Health monitoring, sports tracking, smart notifications, long battery life
Technical Specifications:
- Display: 1.4-inch AMOLED HD display
- Battery Life: 7-day ultra-long battery life
- Water Resistance: 5ATM water resistance rating
- Sensors: Heart rate, blood oxygen, accelerometer, gyroscope
- Connectivity: Bluetooth 5.0, GPS positioning
Usage Scenarios: Sports and fitness, daily commute, business meetings, outdoor adventures
Accessories List: Charging dock, user manual, warranty card
After-Sales Service: 1-year warranty, 30-day no-reason return/exchange, nationwide warranty
Price: 1299 RMB`,
    },
  ],
};

const examplesByTagJapanese = {
  poster: [
    {
      id: 3,
      label: 'アート展覧会',
      prompt:
        'エレガントなアート展覧会のポスターを作成してください。展覧会名は「現代アートギャラリー」、期間は1ヶ月です。',
    },
    {
      id: 1,
      label: 'テックカンファレンス',
      prompt:
        'モダンでミニマルなテックカンファレンスのポスターを作成してください。タイトルは「2025 AIデザインサミット」、場所は北京です。',
    },
    {
      id: 2,
      label: '音楽フェスティバル',
      prompt:
        '活気あふれる音楽フェスティバルのプロモーションポスターをデザインしてください。テーマは「春の音楽祭」、日付は3月15日です。',
    },
  ],
  'social-media': [
    {
      id: 1,
      label: '料理シェア',
      prompt:
        '温かみのある料理シェア用のSNS画像をデザインしてください。テーマは「おうちレシピシェア」です。',
    },
    {
      id: 2,
      label: 'ビジネスイベント',
      prompt:
        'プロフェッショナルなビジネスイベントのプロモーション画像を作成してください。イベント名は「アントレプレナーサミット2025」です。',
    },
    {
      id: 3,
      label: '新商品発表',
      prompt:
        'スタイリッシュな新商品発表用SNS画像を作成してください。新商品は「春限定パフューム」です。',
    },
  ],
  scrollytelling: [
    {
      id: 1,
      label: 'ブランドストーリースクロリーテリング',
      prompt: `ブランドストーリーのためのスクロリーテリング長尺画像をデザインしてください。複数の章に分かれ、それぞれにイラストと簡潔なテキストを含みます。全体のスタイルは温かく癒し系です。

1. 全体スタイル
属性    内容
スタイルポジショニング    山岳美学 × インディーマガジン風 × フレッシュでナチュラルなヒューマニスティックスタイル
キーワード    グリーンライフスタイル、夏の避暑、野生のエレガンス、スロートラベル、フォトジン、田園詩
デザイン特徴    軽やか、余白、密度のバランス、ミニマリズムと触感・質感
適用シーン    季節プロモーション、田舎旅行写真、ブランド写真展ページ、アート系PPT、自然癒し系コースページ`,
    },
    {
      id: 2,
      label: '製品デモインタラクティブページ',
      prompt:
        '製品機能デモ用の長尺画像を作成してください。ステップごとのアニメーションとビジュアルガイドで特徴を強調します。明るい色使いで、オンラインプロモーションに適しています。',
    },
    {
      id: 3,
      label: '公益サイエンス長尺画像',
      prompt:
        '環境保護をテーマにした公益サイエンス長尺画像を制作してください。データビジュアライゼーション、ストーリーイラスト、アクション呼びかけを含みます。スタイルはフレッシュで分かりやすく、SNSシェアに最適です。',
    },
  ],
  infographic: [
    {
      id: 1,
      label: '市場トレンド',
      prompt:
        'データ駆動型の市場トレンドインフォグラフィックを作成してください。テーマは「2025年EC成長レポート」です。',
    },
    {
      id: 2,
      label: '教育コンテンツ',
      prompt:
        '教育知識のインフォグラフィックをデザインしてください。内容は「ごみ分別ガイド」です。',
    },
    {
      id: 3,
      label: '環境統計',
      prompt:
        '環境統計のインフォグラフィックを作成してください。タイトルは「世界の二酸化炭素排出状況」です。',
    },
  ],
  presentation: [
    {
      id: 1,
      label: 'プロジェクト報告書',
      prompt:
        'ビジネススタイルのプロジェクト報告書PPTカバーをデザインしてください。プロジェクト名は「Q4売上サマリー」です。',
    },
    {
      id: 2,
      label: '新商品発表',
      prompt:
        'クリエイティブな新商品発表プレゼンテーションを作成してください。商品は「スマートホームシステム」です。',
    },
    {
      id: 3,
      label: '財務報告書',
      prompt:
        'プロフェッショナルな財務報告書プレゼンテーションを作成してください。タイトルは「2025年年間財務分析」です。',
    },
  ],
};

const updateExamples = () => {
  let examplesByTag;
  if (i18n.locale === 'zh' || i18n.isChinese) {
    examplesByTag = examplesByTagChinese;
  } else if (i18n.locale === 'ja' || i18n.isJapanese) {
    examplesByTag = examplesByTagJapanese;
  } else {
    examplesByTag = examplesByTagEnglish;
  }
  const examples = props.scene?.tag ? examplesByTag[props.scene.tag] || [] : [];
  promptExamples.value = examples.slice(0, 2);
};

watch(() => props.scene, updateExamples, { immediate: true });
watch(() => i18n.isChinese, updateExamples);

const selectExample = (example) => {
  emit('click', example.prompt);
};
</script>
<template>
  <div v-if="promptExamples.length > 0" class="my-4">
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <button
        v-for="example in promptExamples"
        :key="example.id"
        @click="selectExample(example)"
        class="flex items-center justify-between pl-4 pr-2 py-3 bg-white border border-black/5 hover:border-black/10 rounded-[24px] transition-all duration-300 ease-out cursor-pointer group"
      >
        <span
          class="text-sm text-black/50 text-left truncate flex-1 pr-3 group-hover:text-black/90"
        >
          {{
            isChineseLocale
              ? example.prompt
              : example.prompt.substring(0, 60) + (example.prompt.length > 60 ? '...' : '')
          }}
        </span>
        <ArrowUp
          class="w-4 h-4 text-black/40 group-hover:text-emerald-400 transition-colors duration-200 flex-shrink-0"
        />
      </button>
    </div>
  </div>
</template>
